/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react'

import { Group } from '@/entities/groups/models/Group'
import { CurrentScreenType } from '@/entities/userInterface/model/CurrentScreenType'
import { MosaicNode, ToolWidgetType, WidgetNode, WidgetTypeWithSuffix } from '@/shared/components/Mosaic'
import { getToolWidgetNodeFromType } from '@/shared/components/Mosaic/models/ToolWidgetNodeMapping'
import {
  PLANNING_MOSAIC_LAYOUT_LOCAL_STORAGE_KEY, PLANNING_WIDGET_TOOLBAR_LOCAL_STORAGE_KEY, RUNSHEET_MOSAIC_LAYOUT_LOCAL_STORAGE_KEY,
  RUNSHEET_WIDGET_TOOLBAR_LOCAL_STORAGE_KEY,
} from '@/shared/config/consts'
import { getLocalStorageValue, setLocalStorageValue } from '@/shared/lib/localStorageHelpers'
import { EntityId } from '@reduxjs/toolkit'

export type StorageGroupRootNodesMapping = null | {
  [groupId: EntityId]: MosaicNode<WidgetTypeWithSuffix> | null
}

export type StorageGroupWidgetsMapping = null | {
  [groupId: EntityId]: WidgetNode[]
}

// ['Violations', 'Map', 'Geocode', 'A2BRoute', 'Solver']
export const defaultPlanningWidgets: WidgetNode[] = ['Violations', 'Map', 'GanttChart'].map((type) => getToolWidgetNodeFromType(type as ToolWidgetType))
defaultPlanningWidgets.push(getToolWidgetNodeFromType('Map', { location: 'hidden' })) // Add an additional hidden map widget for editing
export const defaultRunsheetWidgets: WidgetNode[] = ['RunsheetBreaks', 'Violations', 'Map', 'GanttChart'].map((type) => getToolWidgetNodeFromType(type as ToolWidgetType))
defaultRunsheetWidgets.push(getToolWidgetNodeFromType('Map', { location: 'hidden' })) // Add an additional hidden map widget for editing

export type OperationWindowLayout = {
  mosaicRootNode: MosaicNode<WidgetTypeWithSuffix> | null
  setRootNode: (node: MosaicNode<WidgetTypeWithSuffix> | null) => void
  widgets: WidgetNode[]
  setWidgets: (widgets: WidgetNode[]) => void
}

/**
 * The following hook is used to manage the layout of the operation window.
 *  It stores the layout in local storage and create a local state to manage the layout to improve performance.
 *  Every 5 seconds, the local storage is updated with the new layout.
 * 
 * Side Note: the layouts are not stored in redux store because the layout is not a global state and it impact performance when resize.
 * 
 * @param groupId the current active groupId retrieved from the redux store
 * @param currentScreen either 'planning' or 'runsheet'
 * @returns OperationWindowLayout
 */
export const useOperationWindowLayout = (groupId: Group['id'], currentScreen: CurrentScreenType): OperationWindowLayout => {
  const isPlanning = currentScreen === 'planning'
  const storageRootNodesKey = isPlanning ? PLANNING_MOSAIC_LAYOUT_LOCAL_STORAGE_KEY : RUNSHEET_MOSAIC_LAYOUT_LOCAL_STORAGE_KEY
  const storageWidgetsKey = isPlanning ? PLANNING_WIDGET_TOOLBAR_LOCAL_STORAGE_KEY : RUNSHEET_WIDGET_TOOLBAR_LOCAL_STORAGE_KEY
  const storageRootNodes = getLocalStorageValue<StorageGroupRootNodesMapping>(storageRootNodesKey, null)
  const storageWidgets = getLocalStorageValue<StorageGroupWidgetsMapping>(storageWidgetsKey, null)

  // To improve grid layout performance, we are separating local storage update from local state update
  const [mosaicRootNode, setRootNode] = useState<MosaicNode<WidgetTypeWithSuffix> | null>(storageRootNodes?.[groupId] || null)
  const [widgets, setWidgets] = useState<WidgetNode[]>(storageWidgets?.[groupId] ||
    isPlanning ? defaultPlanningWidgets : defaultRunsheetWidgets)

  // Update the local storage with the new layout every 5 seconds.
  // This side effect can be extended to update the layout to the server based on userPreferences.
  useEffect(() => {
    const interval = setInterval(() => {
      setLocalStorageValue(storageRootNodesKey, {
        ...storageRootNodes,
        [groupId]: mosaicRootNode,
      })
      setLocalStorageValue(storageWidgetsKey, {
        ...storageWidgets,
        [groupId]: widgets,
      })
    }, 5000)

    return () => clearInterval(interval)
  }, [mosaicRootNode, widgets, groupId, currentScreen])

  // Side Effect when groupId changes
  useEffect(() => {
    setRootNode(storageRootNodes?.[groupId] || null)
    setWidgets(storageWidgets?.[groupId] || (isPlanning ? defaultPlanningWidgets : defaultRunsheetWidgets))
  }, [groupId])

  return {
    mosaicRootNode,
    setRootNode,
    widgets,
    setWidgets,
  }
}
